import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, TextField, Tooltip, IconButton } from '@mui/material';
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium';
import * as React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { showAlert } from '../../context/WarningModalContext';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '../../assets/icons/Delete_Classic.svg';
import { makeStyles } from '@mui/styles';
import ManageSubcriptionPages from '../../assets/images/ManageSubcriptionPages.png';
import Subscribe from '../../assets/images/subscribe.png';
import Unsubscribe from '../../assets/images/unsubscribe.png';

const useStyles = makeStyles((theme) => ({
  root: {
    border: '1px solid #bbbbbb',
    borderRadius: 8,
    background: '#fff',
    padding: '1rem',
    margin: 0,
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  dataGrid: {
    '& .MuiDataGrid-root': {
      border: '1px solid #ccc'
    },
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '##E7E9ED',
      fontSize: '14px',
      fontFamily: "Inter, sans-serif",
      lineHeight: '20px',
      borderBottom: '1px solid #ccc',
      height: '32px !important',
      fontWeight: '600'
    },
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: '#E7E9ED',
      minHeight: '32px !important',
      maxHeight: '32px !important',
      height: '32px !important',
      borderRight: '1px solid #ccc'
    },
    '& .MuiDataGrid-cell': {
      lineHeight: '14px',
      fontWeight: 400,
      fontFamily: "Inter, sans-serif",
      //padding: '10px',
      fontSize: '14px',
      height: '32px',
      overflowWrap: 'anywhere', // force wrap anywhere
      overflow: 'hidden', // hide overflow
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      borderRight: '1px solid #ccc',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      maxWidth: '100%', // ensure cell does not overflow
      minWidth: 0, // allow shrinking
      '& div': {
        display: 'flex',
        //justifyContent: 'center',
        gap: '5px'
      },
      justifyContent: 'center',
      color: '#172B4D'
    },
    '& .MuiDataGrid-row': {
      // borderBottom: '1px solid #ccc'
    },
    '& .MuiDataGrid-row:nth-of-type(even)': {
      backgroundColor: '#F1F2F4'
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: '#F7F8F9'
    },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
      outline: 'none'
    },
    '& .MuiCheckbox-root': {
      padding: '0 4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    '& .MuiDataGrid-cellCheckbox': {
      paddingLeft: '8px'
    },
    '& .MuiDataGrid-iconSeparator': {
      display: 'none'
    },
    '& .MuiDataGrid-sortIcon': {
      opacity: 0.6
    },
    '& .MuiDataGrid-pinnedColumnHeaders, & .MuiDataGrid-pinnedColumnFooter': {
      backgroundColor: '#F7F8F9'
    },
    '& .MuiDataGrid-filler--pinnedRight': {
      backgroundColor: 'white'
    },
    '& .MuiDataGrid-cell[data-field="actions"]': {
      textAlign: 'center',
      display: 'flex',
      justifyContent: 'center'
    },
    '& .MuiDataGrid-cell[data-field="resolutionKeyField"], .MuiDataGrid-cell[data-field="blankAfter"], .MuiDataGrid-cell[data-field="blankBefore"]': {
      alignItems: 'center'
    },
    '& .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell[data-field="delete"]': {
      backgroundColor: '#F1F2F4'
    },
    '& .MuiDataGrid-row:nth-of-type(odd) .MuiDataGrid-cell[data-field="actions"]': {
      backgroundColor: '#F7F8F9'
    },
    '& .MuiDataGrid-main': {
      position: 'unset'
    },
    '& .MuiDataGrid-rowCount': {
      marginRight: '30px'
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '20px',
      fontFamily: "Inter, sans-serif",
      color: '#172B4D'
    },
    '& .invalidCell': {
      border: '2px solid red !important'
    }
  },
  addBtnWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    //marginTop: theme.spacing(1),
    //paddingRight: theme.spacing(1),
    gap: '20px'
  },
  addButton: {
    height: '36px',
    margin: '0px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '5px',
    fontSize: '12px',
    alignItems: 'center',
    textTransform: 'none'
  }
}));

export default function InDataGridContainer({
  columns,
  rows,
  onAdd,
  onDelete,
  onEditCellChange,
  getRowId,
  pageSizeOptions = [25, 50, 100],
  sortable = true,
  searchable = false,
  filterable = true,
  editable = false,
  processRowUpdate,
  onCellEditCommit,
  getDetailPanelRows, // NEW PROP
  apiRef,
  setRows,
  groupingKey,
  addButtonLabel,
  isCellEditable,
  groupingColDef,
  invalidCellIds = [],
  expandedGroups = new Set(),
  setExpandedGroups,
  groupAddButtonLabel,
  onRowEditStop,
  checkboxSelection = false,
  disableSelectionOnClick = true,
  onPopupViewClick, // <-- Added prop
  onRowEdit,
  onRowManage,
  onSubscribe,
  onUnsubscribe,
  isSubscribedEnabled = () => false,
  isUnsubscribedEnabled = () => false,
  showEditButton = () => false,
  showDeleteButton = () => false,
  showManageButton = () => false,
  ...props
}) {
  const classes = useStyles();
  // const apiRef = useGridApiRef();
  //const [rows, setRows] = React.useState(propRows || []);
  const [nextId, setNextId] = React.useState(1000000); // for temp row ids
  const [detailPanelExpandedRow, setDetailPanelExpandedRow] = React.useState(null);
  // const [expandedGroups, setExpandedGroups] = React.useState(new Set(props.expandedGroups || []));
  const [searchText, setSearchText] = React.useState('');

  // State to track child grid edits for expanded row
  const [childGridData, setChildGridData] = React.useState({}); // { [parentRowId]: [childRows] }
  const [copiedRows, setCopiedRows] = React.useState([]);
  const [selectedRows, setSelectedRows] = React.useState([]);
  const isPasteActive = React.useRef(false);

  // React.useEffect(() => {
  //   setRows(propRows || []);
  // }, [propRows]);

  // Sync childGridData with rows when rows change
  React.useEffect(() => {
    if (getDetailPanelRows && Array.isArray(rows)) {
      const newChildData = {};
      rows.forEach((row) => {
        newChildData[getRowId ? getRowId(row) : row.id] = getDetailPanelRows(row) || [];
      });
      setChildGridData(newChildData);
    }
  }, [rows, getDetailPanelRows, getRowId]);

  const handleAddRow = (groupKey = null) => {
    // Create a new row with all fields from columns
    const newRow = { id: nextId };

    columns.forEach(col => {
      if (col.field !== 'actions' && col.field !== 'delete' && col.field !== 'edit') {
        newRow[col.field] = '';
      }
    });
    newRow.isNew = true;
    if (groupKey && typeof groupKey !== 'object') {
      newRow[groupingKey] = groupKey;
    }
    setExpandedGroups && setExpandedGroups((prev) => new Set([...prev, newRow[groupingKey]]));
    setRows((prev) => [newRow, ...prev]); // Add new row at the top
    setNextId((id) => id + 1);
    if (onAdd) onAdd(newRow);
  };

  // Filter rows by search text (matches any cell value)
  const filteredRows = React.useMemo(() => {
    if (!searchText) return rows;
    const lower = searchText.toLowerCase();
    return rows.filter(row =>
      columns.some(col => {
        const value = row[col.field];
        return value && value.toString().toLowerCase().includes(lower);
      })
    );
  }, [searchText, rows, columns]);

  // Ensure 'actions' and 'delete' columns are always last
  const orderedColumns = React.useMemo(() => {
    if (!columns) return [];
    // Only keep one actions column, and do not allow custom delete/edit columns
    const mainCols = columns.filter(col => col.field !== 'actions' && col.field !== 'delete' && col.field !== 'edit')
      .map(col => {
        return {
          ...col,
          hideSortIcons: true,
          renderHeader: (params) => (
            <Tooltip title={params.colDef.headerName} arrow>
              <div style={{
                display: "flex", alignItems: "center", gap: 2, fontSize: '14px',
                fontFamily: "Inter, sans-serif",
                lineHeight: '20px',
                fontWeight: 600, color: '#172B4D',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                maxWidth: col.width || 180 // fallback width
              }}>
                {params.colDef.headerName}
                <div style={{ display: "flex", flexDirection: "column", lineHeight: 0, marginLeft: 2, height: '24px', width: '24px', justifyContent: 'center' }}>
                  <ArrowDropUpIcon
                      style={{
                      fontSize: 24,
                      marginBottom: "-8px",
                      color: params.colDef.sort === "asc" ? "#000" : "#8C9BAB"
                    }}
                  />
                  <ArrowDropDownIcon
                      style={{
                      fontSize: 24,
                      marginTop: "-8px",
                      color: params.colDef.sort === "desc" ? "#000" : "#8C9BAB"
                    }}
                  />
                </div>
              </div>
            </Tooltip>
          ),
          renderCell: (params) => (
            <Tooltip title={params.value} arrow>
              <span style={{
                display: 'inline-block',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                maxWidth: col.width || 180 // fallback width
              }}>
                {params.value}
              </span>
            </Tooltip>
          )
        };
      });
    const actionsCol = columns.find(col => col.field === 'actions');
    const result = actionsCol ? [...mainCols, actionsCol] : mainCols;
    return result;
  }, [columns]);

  // Add a delete button to the Actions column instead of a separate column
  const gridColumns = React.useMemo(() => {
    let cols = orderedColumns.map(col => {
      if (col.field === 'popupview' ) {
        return {
          ...col,
          renderCell: (params) => (
            <Button
              variant="text"
              color="primary"
              onClick={() => onPopupViewClick(params.row) }
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textTransform: 'none',
                padding: 0,
                minWidth: 0,
                width: '100%',
                height: '100%',
              }}
            >
              {col.displayName}
            </Button>
          )
        };
      }
      if (col.field === 'actions') {
        let actions = col.actions ? col.actions.split(',').map(a => a.trim()) : [];
        return {
          ...col,
          renderCell: (params) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
              {params.rowNode?.type === "group" ? (
                <Tooltip title={groupAddButtonLabel}>
                  <AddCircleOutlineIcon
                    fontSize="small"
                    style={{ cursor: 'pointer', color: '#0C66E4', height: '20px', width: '20px' }}
                    onClick={() => handleAddChildRow(params.rowNode.groupingKey)}
                  />
                </Tooltip>
              ) : (
                actions.map(action => {
                  switch (action.toLowerCase()) {
                    case 'edit':
                      return (typeof showEditButton === 'function' && showEditButton(params.row)) ? (
                        <Tooltip title={`Edit ${col.displayName}`} key="edit">
                          <IconButton color="primary" style={{ padding: 0 }} onClick={(e) => handleEditClick(e, params.rowNode.id)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null;
                    case 'delete':
                      return (typeof showDeleteButton === 'function' && showDeleteButton(params.row)) ? (
                         <Tooltip title={`Delete ${col.displayName}`} key="delete">  
                          <IconButton  
                            tabIndex={0}  
                            color="primary"  
                            style={{ padding: 0 }}  
                            onClick={() => { if (onDelete) onDelete(params.row); }}  
                            onKeyDown={(e) => {  
                              if (e.key === 'Enter' || e.key === ' ') {  
                                e.preventDefault();  
                                if (onDelete) onDelete(params.row);  
                              }  
                            }}  
                          >  
                            <img  
                              src={DeleteIcon}  
                              alt="Delete"  
                              style={{ width: '20px', height: '20px' }}  
                            />  
                          </IconButton>  
                        </Tooltip>  
                      ) : null;
                    case 'manage':
                      return (typeof showManageButton === 'function' && showManageButton(params.row)) ? (
                         <Tooltip title={`Manage ${col.displayName}`} key="manage">  
                          <IconButton  
                            tabIndex={0}  
                            color="primary"  
                            style={{ padding: 0 }}  
                            onClick={(e) => {  
                              e && e.stopPropagation();  
                              if (onRowManage) {  
                                const id = params.rowNode?.id ?? (getRowId ? getRowId(params.row) : params.id);  
                                onRowManage(id, params.row, 'manage');  
                              }  
                            }}  
                            onKeyDown={(e) => {  
                              if (e.key === 'Enter' || e.key === ' ') {  
                                e.preventDefault();  
                                e.stopPropagation();  
                                if (onRowManage) {  
                                  const id = params.rowNode?.id ?? (getRowId ? getRowId(params.row) : params.id);  
                                  onRowManage(id, params.row, 'manage');  
                                }  
                              }  
                            }}  
                          >  
                            <img  
                              src={ManageSubcriptionPages}  
                              alt="Manage"  
                              style={{ width: '20px', height: '20px' }}  
                            />  
                          </IconButton>  
                        </Tooltip> 
                      ) : null;
                    case 'subscribe':
                      return (typeof isSubscribedEnabled === 'function' && isSubscribedEnabled(params.row)) ? (
                        <Tooltip title={`Subscribe ${col.displayName}`} key="subscribe">  
                          <IconButton  
                            tabIndex={0}  
                            color="primary"  
                            style={{ padding: 0 }}  
                            onClick={() => { if (onSubscribe) onSubscribe(params.row); }}  
                            onKeyDown={(e) => {  
                              if (e.key === 'Enter' || e.key === ' ') {  
                                e.preventDefault();  
                                if (onSubscribe) onSubscribe(params.row);  
                              }  
                            }}  
                          >  
                            <img  
                              src={Subscribe}  
                              alt="Subscribe"  
                              style={{ width: '20px', height: '20px' }}  
                            />  
                          </IconButton>  
                        </Tooltip>  
                      ) : null;  
                    case 'unsubscribe':
                      return (typeof isUnsubscribedEnabled === 'function' && isUnsubscribedEnabled(params.row)) ? (
                        <Tooltip title={`Unsubscribe ${col.displayName}`} key="unsubscribe">  
                          <IconButton  
                            tabIndex={0}  
                            color="primary"  
                            style={{ padding: 0 }}  
                            onClick={() => { if (onUnsubscribe) onUnsubscribe(params.row); }}  
                            onKeyDown={(e) => {  
                              if (e.key === 'Enter' || e.key === ' ') {  
                                e.preventDefault();  
                                if (onUnsubscribe) onUnsubscribe(params.row);  
                              }  
                            }}  
                          >  
                            <img  
                              src={Unsubscribe}  
                              alt="Unsubscribe"  
                              style={{ width: '20px', height: '20px' }}  
                            />  
                          </IconButton>  
                        </Tooltip>  
                      ) : null;
                    default:
                      // For any other action, just render nothing (or you could render a generic button/icon if desired)
                      return null;
                  }
                }).filter(Boolean)
              )}
            </div>
          )
        };
      }
      return col;
    });
    return cols;
  }, [orderedColumns, onDelete, classes.addButton, getRowId]);

  const groupColDef = React.useMemo(() => {
    if (groupingColDef) {
      return {
        ...groupingColDef,
        hideSortIcons: true,
        renderHeader: (params) => (
          <div style={{
            display: "flex", alignItems: "center", gap: 2, fontSize: '14px',
            fontFamily: "Inter, sans-serif",
            lineHeight: '20px',
            fontWeight: 600, color: '#172B4D'
          }}
          >
            {params.colDef.headerName}
            <div style={{ display: "flex", flexDirection: "column", lineHeight: 0, marginLeft: 2, height: '24px', width: '24px', justifyContent: 'center' }}>
              <ArrowDropUpIcon
                  style={{
                  fontSize: 24,
                  marginBottom: "-8px",
                  color: params.colDef.sort === "asc" ? "#000" : "#8C9BAB"
                }}
              />
              <ArrowDropDownIcon
                  style={{
                  fontSize: 24,
                  marginTop: "-8px",
                  color: params.colDef.sort === "desc" ? "#000" : "#8C9BAB"
                }}
              />
            </div>
          </div>
        )
      };
    }
    return null;
  }, [groupingColDef]);

  const handleEditClick = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    // on edit row is getting selected but i want prevent onclick
    //apiRef.current.setRowSelected(id, false);

    //check if row is already in edit mode
    const isEditing = apiRef.current.state.editRows[id];
    if (isEditing) {
      // Only stop if actually in edit mode  
      if (Object.keys(apiRef.current.state.editRows[id]).length > 0) {  
        apiRef.current.stopRowEditMode({ id });  
      }
    } else {
      apiRef.current.startRowEditMode({ id }); // 🔥 Make row editable
    }
    if (onRowEdit) {
      const rowData = apiRef.current.getRow(id);
      onRowEdit(id, rowData);
    }
  };

  // Handler for child grid add row
  const handleAddChildRow = (parentId) => {
    handleAddRow(parentId);
    // setChildGridData((prev) => {
    //   const nextId = (prev[parentId]?.length || 0) + 1 + Math.floor(Math.random() * 10000);
    //   const newRow = { id: nextId, tableName: '', fieldName: '', resolutionKeyField: '', expression: '', isNew: true };
    //   const updated = { ...prev, [parentId]: [...(prev[parentId] || []), newRow] };
    //   // Update parent row's details in main grid
    //   setRows((mainRows) => mainRows.map(r => (r.id === parentId ? { ...r, details: updated[parentId] } : r)));
    //   return updated;
    // });
  };

  // Handler for child grid delete row
  const handleDeleteChildRow = (parentId, childId) => {
    setChildGridData((prev) => {
      const updatedRows = (prev[parentId] || []).filter(r => r.id !== childId);
      const updated = { ...prev, [parentId]: updatedRows };
      setRows((mainRows) => mainRows.map(r => (r.id === parentId ? { ...r, details: updatedRows } : r)));
      return updated;
    });
  };

  // Handler for child grid inline edit
  const handleChildGridEdit = (parentId, params) => {
    setChildGridData((prev) => {
      const updatedRows = (prev[parentId] || []).map(r => (r.id === params.id ? { ...r, ...params } : r));
      const updated = { ...prev, [parentId]: updatedRows };
      setRows((mainRows) => mainRows.map(r => (r.id === parentId ? { ...r, details: updatedRows } : r)));
      return updated;
    });
  };

  // Child grid columns for detail panel (with delete and editable)
  const detailPanelColumns = [
    { field: 'tableName', headerName: 'Table Name', flex: 1, minWidth: 120, editable: true },
    { field: 'fieldName', headerName: 'Field Name', flex: 1, minWidth: 120, editable: true },
    { field: 'resolutionKeyField', headerName: 'Resolution Key Field', flex: 1, minWidth: 150, editable: true },
    { field: 'expression', headerName: 'Expression', flex: 2, minWidth: 200, editable: true },
    {
      field: 'delete',
      headerName: '',
      width: 52,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
            onClick={() => handleDeleteChildRow(params.row.parentId, params.id)}
            className={classes.addButton}
            color="error"
            size="small"
            startIcon={<DeleteIcon fontSize="small" />}
        >
          Delete
        </Button>
      )
    }
  ];

  // // Detail panel content renderer (with add, delete, edit)
  // const getDetailPanelContent = React.useCallback((params) => {
  //   if (!getDetailPanelRows) return null;
  //   const parentId = getRowId ? getRowId(params.row) : params.row.id;
  //   const childRows = (childGridData[parentId] || []).map(r => ({ ...r, parentId }));
  //   return (
  //     <div style={{ padding: 16, background: '#fafbfc' }}>
  //       <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
  //         <Button
  //           onClick={() => handleAddChildRow(parentId)}
  //           className={classes.addButton}
  //           color="primary"
  //           size="small"
  //           startIcon={<AddCircleOutlineIcon fontSize="small" />}
  //         >
  //           Add Row
  //         </Button>
  //       </div>
  //       <div style={{ overflowY: 'auto' }}>
  //         <DataGridPremium
  //           autoHeight
  //           density="compact"
  //           columns={detailPanelColumns}
  //           rows={childRows}
  //           hideFooter
  //           disableColumnMenu
  //           disableColumnFilter
  //           disableSelectionOnClick
  //           sx={{ border: 0, background: '#fafbfc' }}
  //           processRowUpdate={(updatedRow) => {
  //             handleChildGridEdit(parentId, updatedRow);
  //             return updatedRow;
  //           }}
  //           experimentalFeatures={{ newEditingApi: true }}
  //           getRowHeight={() => 'auto'}
  //         />
  //       </div>
  //     </div>
  //   );
  // }, [getDetailPanelRows, childGridData, classes.addButton, handleAddChildRow, handleDeleteChildRow, handleChildGridEdit, getRowId]);

  // // Detail panel height (auto or fixed)
  // const getDetailPanelHeight = React.useCallback((params) => {
  //   if (!getDetailPanelRows) return 0;
  //   const childRows = getDetailPanelRows(params.row) || [];
  //   // 40px per row + header (40) + padding (32)
  //   return Math.max(80, 40 * (childRows.length + 1) + 32);
  // }, [getDetailPanelRows]);

  // // Handler to expand/collapse detail panel when Sr. No. cell is clicked
  // const handleCellClick = React.useCallback((params) => {
  //   if (params.field === columns[0]?.field) { // Sr. No. assumed to be first column
  //     setDetailPanelExpandedRow((prev) => (prev === params.id ? null : params.id));
  //   }
  //   if (props.onCellClick) props.onCellClick(params);
  // }, [columns, props]);

  // Checks if two row objects are different (shallow compare for all fields)
  function isRowChanged(oldRow, newRow) {
    if (!oldRow || !newRow) return false;
    const keys = new Set([...Object.keys(oldRow), ...Object.keys(newRow)]);
    for (const key of keys) {
      if (oldRow[key] !== newRow[key]) return true;
    }
    return false;
  }

  const onCreateCopy = () => {
    showAlert(
      `Are you sure you want to create a copy of selected Tenants?`,
      'Confirm Creation',
      'warning',
      true,
      'No',
      'Yes',
      () => handleCreateCopy(),
      () => { },
      false,
      undefined
    );
  };

  // --- CREATE COPY ---
  const handleCreateCopy = () => {
    const selected = rows.filter((r) => selectedRows.includes(r.id));
    if (!selected.length) return;
    isPasteActive.current = true;
    console.log('Creating copy of rows:', selected);
    const newRows = selected.map((r) => ({
      ...r,
      id: uuidv4(),
      isNew: true,
      _isPasted: true
    }));

    setRows((prev) => [...newRows, ...prev]);
    setSelectedRows([]);

    // Reset after short delay
    setTimeout(() => {
      isPasteActive.current = false;
    }, 0);
  };

  // Custom processRowUpdate for DataGridPremium Calls parent's onRowUpdate if row is actually changed

  function customProcessRowUpdate(newRow, oldRow) {
    if (isRowChanged(oldRow, newRow)) {
      // if (typeof processRowUpdate === 'function') {
      newRow.isEdited = true;
      console.log('Updating row:', rows);
      setRows((prevRows) =>
        prevRows.map((row) =>
          (getRowId ? getRowId(row) : row.id) === (getRowId ? getRowId(newRow) : newRow.id) ? newRow : row
        )
      );
      processRowUpdate && processRowUpdate(newRow, oldRow);
      //}
    }
    return newRow;
  }

  const handleGroupExpansionChange = (params) => {
    const groupName = params.groupingKey;
    setExpandedGroups && setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) newSet.delete(groupName);
      else newSet.add(groupName);
      return newSet;
    });
  };

  const handleRowEditStop = (params) => {
    // Ensure at least one cell is focused after row edit stops
    onRowEditStop && onRowEditStop(params);

  };

  return (
    <Box className={classes.root} style={{ display: 'flex', flexDirection: 'column',height: '53vh'  }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <div className={classes.searchWrapper} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <TextField
              sx={{ width: '250px' }}
              label="Search"
              placeholder="Search here..."
              variant="outlined"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
          />
          {/* <Button */}
              {/* variant="contained"
              onClick={(e) => setSearchText(e.target.value)}
              sx={{ ml: 2, height: '56px', backgroundColor: '#0C66E4' }}
              style={{
              minWidth: '63px',
              height: '28px',
              fontSize: '12px',
              textTransform: 'none',
              lineHeight: '18px',
              padding: '4px 12px'
            }} */}
          {/* > */}
            {/* Search */}
          {/* </Button> */}
        </div>
        {addButtonLabel &&
          <div className={classes.addBtnWrapper}>
            {selectedRows.length > 0 &&
              <Tooltip title="Create Copy">
                <IconButton color="primary" style={{ padding: 0 }} onClick={onCreateCopy}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            }
            <Tooltip title={addButtonLabel} arrow>
              <Button
                  onClick={handleAddRow}
                  className={classes.addButton}
                  color="primary"
                  size="small"
                  style={{ minWidth: 36, padding: 0 }}
              >
                <AddCircleOutlineIcon fontSize="medium" style={{ marginRight: '20px' }} />
              </Button>
            </Tooltip>
          </div>
        }
      </div>
      {/* DataGridPremium should NOT be inside a FormControl either */}
      <div style={{ flex: 1, minHeight: 0, width: '100%' }}>
        <DataGridPremium
            columnHeaderHeight={0}
            apiRef={apiRef}
            columns={gridColumns}
            rows={filteredRows}
            pagination={true}
            initialState={{
            pagination: { paginationModel: { pageSize: 5 } },
          }}
            pageSizeOptions={[5, 10, 25, { value: -1, label: 'All' }]}
            sortingOrder={sortable ? ['asc', 'desc'] : []}
            disableColumnFilter={!filterable}
            disableColumnMenu={!sortable}
            pinnedColumns={{ right: ['actions'] }}
            experimentalFeatures={editable ? { newEditingApi: true, rowGrouping: true, clipboardPaste: true } : {}}
            processRowUpdate={editable ? customProcessRowUpdate : undefined}
            onCellEditCommit={editable && onCellEditCommit ? onCellEditCommit : undefined}
          // pinnedColumns={{ right: ['actions'] }}
          // getDetailPanelContent={getDetailPanelRows ? getDetailPanelContent : undefined}
          // getDetailPanelHeight={getDetailPanelRows ? getDetailPanelHeight : undefined}
            getRowHeight={() => 'auto'}
            editMode="cell"
            isCellEditable={isCellEditable}
            onCellClick={(params, event) => {
            // if (editable && params.isEditable) {
            //   apiRef.current.setCellFocus(params.id, params.field);
            //   apiRef.current.startCellEditMode({ id: params.id, field: params.field });
            // }
            if (props.onCellClick) props.onCellClick(params, event);
          }}
            onRowClick={(params) => {
            if (params.field === columns[0]?.field) {
              setDetailPanelExpandedRow((prev) => (prev === params.id ? null : params.id));
            }
          }}
            isGroupExpandedByDefault={(node) => expandedGroups.has(node.groupingKey)}
            onRowGroupExpansionChange={handleGroupExpansionChange}
            onRowEditStop={handleRowEditStop}
            {...props}
            className={classes.dataGrid}
            groupingColDef={groupColDef}
            getCellClassName={(params) => {
            const isInvalid = invalidCellIds.some(
              (item) => item.id === params.id && item.field === params.field
            );
            return isInvalid ? "invalidCell" : "";
          }}
            //checkboxSelection={checkboxSelection}
            //disableSelectionOnClick={false}
            //onRowSelectionModelChange={(ids) => setSelectedRows(ids)}
            //rowSelectionModel={[selectedRows]}
        />
      </div>       
    </Box>
  );
}

